// =============================================================================
// features/sales/agenda/labels.ts — Rótulos pt-BR e urgência da agenda
// comercial. Funções puras — ver __tests__/labels.test.ts.
// =============================================================================

import type { Appointment, ContactChannel } from '@elemento/shared-schemas';

import type { BadgeVariant } from '../../../components/ui/Badge';

import { AGORA_WINDOW_MINUTES } from './buckets';
import { addMinutes, formatDateOrgTz, formatTimeOrgTz } from './timezone';

// ---------------------------------------------------------------------------
// Canal de contato
// ---------------------------------------------------------------------------

export const CHANNEL_LABELS: Record<ContactChannel, string> = {
  phone_manual: 'Ligação',
  phone_integrated: 'Ligação (telefonia)',
  whatsapp: 'WhatsApp',
  in_person: 'Presencial',
  other: 'Outro',
};

// ---------------------------------------------------------------------------
// Urgência — badge "vencido"/"agora" nunca só por cor (doc 26 §7).
//
// Regra inegociável (DoD do slot): o atraso vem SEMPRE de
// `appointment.isOverdue` (calculado pela API), nunca recomputado aqui a
// partir de `Date.now()` local — evita divergência de relógio de cliente e
// mantém a mesma fonte de verdade da lista e dos indicadores.
// ---------------------------------------------------------------------------

export type UrgencyLevel = 'overdue' | 'imminent' | 'normal';

/**
 * "Iminente" usa a MESMA janela que a aba/indicador "Agora" (buckets.ts) —
 * fonte única, para as duas noções de "precisa de ação em breve" nunca
 * descasarem (achado de revisão F33-S05: eram duas constantes independentes
 * com o mesmo valor por coincidência).
 */
const IMMINENT_WINDOW_MINUTES = AGORA_WINDOW_MINUTES;

/**
 * Nível de urgência de um compromisso agendado, para colorir o card/badge.
 * `now` é usado só para "imminent" (vence nos próximos IMMINENT_WINDOW_MINUTES
 * min) — "overdue" sempre vem de `isOverdue` da API.
 */
export function urgencyLevel(appointment: Appointment, now: Date): UrgencyLevel {
  if (appointment.status !== 'scheduled') return 'normal';
  if (appointment.isOverdue) return 'overdue';

  const scheduledAt = new Date(appointment.scheduledAt);
  if (scheduledAt <= addMinutes(now, IMMINENT_WINDOW_MINUTES)) return 'imminent';
  return 'normal';
}

export const URGENCY_BADGE_VARIANT: Record<UrgencyLevel, BadgeVariant> = {
  overdue: 'danger',
  imminent: 'warning',
  normal: 'neutral',
};

export const URGENCY_LABEL: Record<UrgencyLevel, string> = {
  overdue: 'Atrasado',
  imminent: 'Em breve',
  normal: 'Agendado',
};

// ---------------------------------------------------------------------------
// "Próxima ação" — rótulo relativo de quando/quanto falta, combinado com o
// canal (doc 26 §6.1 lista "próxima ação" como coluna própria, separada de
// horário/motivo/canal — aqui ela vira "o que fazer e quando", legível sem
// precisar cruzar as outras colunas mentalmente).
// ---------------------------------------------------------------------------

function minutesBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / 60_000);
}

/** "Ligar — atrasado há 40 min" / "WhatsApp — em 25 min" / "Presencial — hoje às 15:30" / "Ligação — amanhã". */
export function nextActionLabel(appointment: Appointment, now: Date): string {
  const channelLabel = CHANNEL_LABELS[appointment.channel];
  if (appointment.status !== 'scheduled') {
    return channelLabel;
  }

  const scheduledAt = new Date(appointment.scheduledAt);
  const diffMinutes = minutesBetween(now, scheduledAt); // positivo = futuro

  if (appointment.isOverdue) {
    const lateBy = Math.abs(diffMinutes);
    const lateLabel = lateBy < 60 ? `${lateBy} min` : `${Math.round(lateBy / 60)}h`;
    return `${channelLabel} — atrasado há ${lateLabel}`;
  }

  if (diffMinutes <= IMMINENT_WINDOW_MINUTES) {
    return `${channelLabel} — em ${diffMinutes} min`;
  }

  const sameDay = formatDateOrgTz(appointment.scheduledAt) === formatDateOrgTz(now.toISOString());
  if (sameDay) {
    return `${channelLabel} — hoje às ${formatTimeOrgTz(appointment.scheduledAt)}`;
  }

  return `${channelLabel} — ${formatDateOrgTz(appointment.scheduledAt)} às ${formatTimeOrgTz(appointment.scheduledAt)}`;
}
